/**
 * SERVICES — individual clinical treatment pages.
 *
 * Each entry becomes a route at /services/<slug> with its own SEO metadata,
 * Service schema, FAQ block and internal linking. Split out of /facials to
 * capture high-intent long-tail queries like "microneedling melbourne",
 * "LED facial melbourne", "hydrodermabrasion melbourne", etc.
 *
 * Keep this file in sync with the sitemap + generate-static-routes.js.
 */
export const SERVICES = {
  glow: {
    slug: 'glow',
    h1: 'The Glow Facial',
    seoTitle: 'The Glow Facial Melbourne | 45-Minute Express Facial | AT THE BEAUTY BAR',
    seoDesc: 'A 45-minute clinical express facial in Melbourne. Deep cleanse, gentle exfoliation, sculpting facial massage and cold-hammer finish. Perfect lunch-break glow at our Caulfield South studio.',
    keywords: 'glow facial melbourne, express facial melbourne, lunchtime facial melbourne, 45 minute facial melbourne, facial caulfield south, quick facial before event, radiance facial melbourne, brightening facial melbourne',
    duration: '45 minutes',
    priceFrom: 80,
    tagline: 'The 45-minute glow reset',
    intro: 'A precision-designed 45-minute facial for the days you have limited time but need to show up looking your best. Deep cleanse, gentle enzymatic exfoliation, hand-sculpting facial massage and a cooling cold-hammer finish. Perfect before a wedding, event, first date or Monday morning meeting.',
    benefits: [
      'Immediate visible brightness and hydration',
      'Reduced puffiness around eyes and jaw',
      'Sculpted, contoured appearance from professional massage',
      'Zero downtime — walk out ready to wear makeup',
    ],
    process: [
      'Skin analysis under proper lighting and a warm greeting to settle in',
      'Double cleanse tailored to your skin type on the day',
      'Gentle enzymatic exfoliation to lift dead cells',
      '15-minute sculpting facial massage — the ritual most clients call life-changing',
      'Cooling cold-hammer therapy to depuff and tighten',
      'Custom serum and moisturiser application, SPF finish',
    ],
    idealFor: 'Anyone needing a glow reset before an event, first-time clients wanting to try a boutique facial, and busy professionals who cannot commit to 90 minutes.',
    schemaType: 'BeautySalon',
    relatedServices: ['reset', 'signature', 'ultimate'],
  },
  reset: {
    slug: 'reset',
    h1: 'The Reset Facial',
    seoTitle: 'The Reset Facial Melbourne | 60-Minute Clinical Facial | AT THE BEAUTY BAR',
    seoDesc: 'A 60-minute clinical reset facial in Melbourne. Microcurrent cleansing, enzyme resurfacing, sculpting massage and LED therapy. Suitable for sensitive and rosacea-prone skin.',
    keywords: 'reset facial melbourne, clinical facial melbourne, 60 minute facial melbourne, sensitive skin facial melbourne, LED facial melbourne, calming facial melbourne, rosacea facial melbourne, facial caulfield south',
    duration: '60 minutes',
    priceFrom: 120,
    tagline: 'A 60-minute clinical reset for skin that needs a full recalibration',
    intro: 'When your skin feels off — reactive, congested, dehydrated, dull — the Reset Facial gives it a proper 60-minute recalibration. Microcurrent cleansing lifts congestion, enzyme resurfacing clears dead cells without abrasion, sculpting massage improves circulation, and 15 minutes of LED therapy repairs your barrier from the inside out.',
    benefits: [
      'Restored barrier function within 24 hours',
      'Visible calm on rosacea-prone and reactive skin',
      'Deep decongestion without stripping',
      'Compounding results with a series — regular clients pair with monthly LED',
    ],
    process: [
      'Detailed consultation on how your skin has been behaving',
      'Double cleanse with a barrier-safe formulation',
      'Microcurrent-assisted deep cleanse to lift congestion painlessly',
      'Gentle enzymatic resurfacing (not acid) for reactive skin',
      'Serum tailored to the day — B3 for redness, Vitamin C for dullness, hyaluronic for dehydration',
      '20-minute LED therapy (red, blue or combo depending on concern)',
      'Custom moisturiser and SPF',
    ],
    idealFor: 'Sensitive skin, rosacea-prone skin, first-time clinical facial clients, monthly maintenance appointments.',
    schemaType: 'BeautySalon',
    relatedServices: ['glow', 'signature', 'skin-correction'],
  },
  'skin-correction': {
    slug: 'skin-correction',
    h1: 'The Skin Correction (Acne) Facial',
    seoTitle: 'Acne Treatment Facial Melbourne | Skin Correction Facial | AT THE BEAUTY BAR',
    seoDesc: 'Clinical acne treatment facial in Melbourne. Hydrodermabrasion, high-frequency wand, Vitamin-C infusion and Blue LED to clear active breakouts. In Caulfield South.',
    keywords: 'acne treatment melbourne, acne facial melbourne, adult acne treatment melbourne, blue LED facial melbourne, hydrodermabrasion melbourne, hormonal acne facial melbourne, breakout facial melbourne, facial for acne caulfield south',
    duration: '60-75 minutes',
    priceFrom: 140,
    tagline: 'A targeted 60-75 minute clinical protocol for active breakouts',
    intro: 'Adult and hormonal acne needs a fundamentally different approach to teenage acne — stripping the skin often makes it worse. Our Skin Correction facial uses hydrodermabrasion to gently decongest, a high-frequency wand to kill acne bacteria without heat, Vitamin-C infusion to accelerate healing, and 25 minutes of Blue LED therapy to break the inflammation cycle.',
    benefits: [
      'Immediate reduction in inflammation and redness',
      'Cleared congestion without post-facial breakouts',
      'Kills P. acnes bacteria at the source',
      'Accelerates healing of existing spots and prevents new ones',
    ],
    process: [
      'Consultation covering hormonal patterns, diet, stress and current routine',
      'Double cleanse with an acne-safe formulation',
      'Hydrodermabrasion — vacuum-based decongestion, no manual extractions',
      'High-frequency wand across active breakouts',
      'Vitamin-C infusion via microcurrent',
      '25 minutes of Blue LED therapy (415 nm) to kill P. acnes bacteria',
      'Non-comedogenic moisturiser and SPF, at-home routine written up for you',
    ],
    idealFor: 'Adult acne (25+), hormonal acne around the jaw, PCOS-related breakouts, congestion that products alone cannot shift, first-time skincare consultations after over-the-counter routines have failed.',
    schemaType: 'MedicalTherapy',
    relatedServices: ['reset', 'signature', 'microneedling'],
  },
  signature: {
    slug: 'signature',
    h1: 'The Signature Facial',
    seoTitle: 'Signature Facial Melbourne | 90-Minute Clinical Facial | AT THE BEAUTY BAR',
    seoDesc: 'Our signature 75-90 minute clinical facial in Melbourne. RF firming, microcurrent, cold-hammer, hydrodermabrasion and LED therapy. Beauty bar Caulfield South.',
    keywords: 'signature facial melbourne, best facial melbourne, RF facial melbourne, radiofrequency facial melbourne, microcurrent facial melbourne, luxury facial melbourne, boutique facial caulfield south, 90 minute facial melbourne',
    duration: '75-90 minutes',
    priceFrom: 170,
    tagline: 'The full clinical experience — five treatment technologies in one facial',
    intro: 'Our most-booked facial for a reason. Every clinical technology in the studio deployed in a single 90-minute treatment: radiofrequency for firming, microcurrent for muscle re-education, cold-hammer for sculpting, hydrodermabrasion for decongestion, and LED for cellular repair. Comprehensive skincare designed around your specific concerns on the day.',
    benefits: [
      'Immediate visible firming and lift from RF + microcurrent',
      'Sculpted, snatched appearance for 5-7 days',
      'Deeply decongested skin without inflammation',
      'Improves compound with every session — regulars come monthly',
    ],
    process: [
      'Full consultation and skin analysis',
      'Double cleanse and enzymatic exfoliation',
      'Hydrodermabrasion for decongestion',
      'Radiofrequency (RF) firming — 15 minutes on face and neck',
      'Microcurrent muscle stimulation — the "workout" for facial muscles',
      'Cold-hammer sculpting and de-puffing',
      'Serum infusion tailored to your skin on the day',
      '20 minutes of LED therapy',
      'Custom moisturiser, eye treatment, and SPF finish',
    ],
    idealFor: 'Monthly maintenance for skin over 35, pre-event skin prep (weddings, milestone birthdays), and clients looking for meaningful results without moving to microneedling yet.',
    schemaType: 'BeautySalon',
    relatedServices: ['ultimate', 'microneedling', 'skin-correction'],
  },
  microneedling: {
    slug: 'microneedling',
    h1: 'Microneedling — Collagen Induction Therapy',
    seoTitle: 'Microneedling Melbourne | Collagen Induction Therapy | AT THE BEAUTY BAR',
    seoDesc: 'Medical-grade microneedling in Melbourne. Collagen Induction Therapy with RF and LED support. Real collagen change, honest timelines, LED-supported recovery. Caulfield South studio.',
    keywords: 'microneedling melbourne, collagen induction therapy melbourne, medical microneedling melbourne, RF microneedling melbourne, skin needling melbourne, acne scar treatment melbourne, microneedling caulfield south, professional microneedling near me',
    duration: '75-90 minutes',
    priceFrom: 230,
    tagline: 'The single highest-ROI treatment for skin over 35',
    intro: 'Medical-grade collagen induction therapy. Thousands of controlled micro-channels trigger your body\'s natural wound-healing response, producing measurable increases in new collagen and elastin over the following 6-12 weeks. Combined with radiofrequency for enhanced firming and LED for accelerated recovery. Independent clinical studies have measured up to 400% increases in collagen production six months after a proper series.',
    benefits: [
      'Genuine, measurable collagen change (not just surface improvement)',
      'Softens fine lines and improves skin firmness',
      'Reduces acne scarring and enlarged pores',
      'Improves overall skin texture and radiance',
      'Compounding permanent results — every session builds on the last',
    ],
    process: [
      'Detailed consultation and topical numbing (30 minutes)',
      'Double cleanse and skin prep',
      'Microneedling pass across face and neck (adjustable depth 0.5-2.5 mm)',
      'Optional RF integration for enhanced firming',
      'Post-treatment mask to calm and hydrate',
      '20 minutes of Red LED therapy to accelerate healing',
      'Recovery balm application and at-home aftercare protocol',
    ],
    idealFor: 'Skin over 30, loss of firmness, acne scarring from earlier decades, fine lines around eyes and mouth, enlarged pores, dull texture that hasn\'t responded to regular facials.',
    downtime: '24-48 hours of pink, warm skin. Fully back to normal within 3 days. Best scheduled Thursday for full recovery by weekend.',
    frequency: '4-6 sessions spaced 4-6 weeks apart for the initial series, then maintenance every 3-4 months.',
    schemaType: 'MedicalTherapy',
    relatedServices: ['signature', 'ultimate', 'skin-correction'],
  },
  ultimate: {
    slug: 'ultimate',
    h1: 'The Ultimate Experience Facial',
    seoTitle: 'Ultimate Experience Facial Melbourne | 90-Minute Murad Facial | AT THE BEAUTY BAR',
    seoDesc: 'Our flagship 90-minute Murad TechnoCeuticals facial in Melbourne. Every clinical technology, tailored serum protocol, LED, microcurrent and cold hammer. Caulfield South.',
    keywords: 'ultimate facial melbourne, murad facial melbourne, techoceuticals facial melbourne, luxury facial melbourne, 90 minute facial melbourne, best clinical facial melbourne, flagship facial caulfield south, murad technoceuticals melbourne',
    duration: '90 minutes',
    priceFrom: 265,
    tagline: 'Our flagship 90-minute Murad TechnoCeuticals treatment',
    intro: 'The most comprehensive facial in the studio. A full 90 minutes with every clinical technology, the complete Murad TechnoCeuticals serum library, and a Vitamin-C or hydro-jelly infusion mask. Designed for clients who want the deepest level of monthly maintenance and results.',
    benefits: [
      'Deepest hydration and barrier repair possible in a single session',
      'Full Murad TechnoCeuticals serum protocol tailored to your skin',
      'Immediate visible lift, glow and firmness',
      'The anchor treatment for our monthly membership regulars',
    ],
    process: [
      'Extended consultation covering routine, lifestyle and recent stressors',
      'Double cleanse with the most-appropriate Murad cleansing formulation',
      'Enzymatic resurfacing or micro-exfoliation',
      'Hydrodermabrasion for deep decongestion',
      'Murad TechnoCeuticals serum layering — up to 4 actives in sequence',
      'Radiofrequency, microcurrent and cold-hammer treatment protocol',
      'Vitamin-C or hydro-jelly infusion mask (chosen based on skin on the day)',
      '20 minutes of LED therapy',
      'Complete finishing routine with eye treatment, SPF and lip care',
    ],
    idealFor: 'Monthly maintenance clients over 40, high-value pre-event skin prep, and anyone wanting the most comprehensive single-session results available in a boutique studio.',
    schemaType: 'BeautySalon',
    relatedServices: ['signature', 'microneedling', 'reset'],
  },
  'led-therapy': {
    slug: 'led-therapy',
    h1: 'LED Light Therapy',
    seoTitle: 'LED Light Therapy Melbourne | Red & Blue Light Facial | AT THE BEAUTY BAR',
    seoDesc: 'Clinical LED light therapy in Melbourne. Red for anti-ageing, blue for acne, near-infrared for barrier repair. Standalone sessions and add-on to every facial.',
    keywords: 'LED light therapy melbourne, red light therapy melbourne, blue light therapy melbourne, LED facial melbourne, near-infrared therapy melbourne, LED acne treatment melbourne, LED anti-ageing melbourne, LED caulfield south',
    duration: '20-30 minutes',
    priceFrom: 35,
    tagline: 'The clinical light therapy that supports every other treatment',
    intro: 'LED light therapy sounds gentle because it is painless and needs no downtime, but the mitochondrial biology is well-established. Red light (630-660 nm) stimulates collagen production, near-infrared (830-850 nm) penetrates deeper for cellular repair, and blue light (415 nm) kills the P. acnes bacteria responsible for breakouts. Standalone sessions or as an add-on to every clinical facial.',
    benefits: [
      'Accelerates collagen production and barrier repair',
      'Calms redness and rosacea flares',
      'Kills acne-causing bacteria (blue light)',
      'Reduces post-treatment downtime by up to 40%',
      'No downtime — walk out ready for makeup',
    ],
    process: [
      'Cleanse and skin prep',
      'Positioning under the medical-grade LED panel',
      '20-30 minutes of your chosen wavelength (red / blue / near-infrared / combo)',
      'Optional serum application before or after for enhanced penetration',
    ],
    idealFor: 'Anyone as an add-on to a facial. Standalone LED is ideal for post-treatment recovery, ongoing anti-ageing maintenance, active acne breakouts, and clients with skin too sensitive for more aggressive treatments.',
    frequency: 'For active concerns: 2-3 sessions per week for 4-6 weeks. For maintenance: every visit as a facial add-on.',
    schemaType: 'MedicalTherapy',
    relatedServices: ['reset', 'skin-correction', 'signature'],
  },
};

export const SERVICE_LIST = Object.values(SERVICES);
